// Dependency-free arbitrary-precision digit generators for pi, e and the
// golden ratio. Everything uses the BigInt constructor form (not `10n`
// literals) so it compiles under the project's ES2017 target.

export type MathConstant = 'pi' | 'e' | 'phi';

export const MAX_DIGITS = 5000;

export const CONSTANT_META: Record<MathConstant, { label: string; symbol: string; blurb: string }> =
  {
    pi: {
      label: 'Pi (π)',
      symbol: 'π',
      blurb: 'The ratio of a circle’s circumference to its diameter.',
    },
    e: {
      label: 'Euler’s number (e)',
      symbol: 'e',
      blurb: 'The base of the natural logarithm.',
    },
    phi: {
      label: 'Golden ratio (φ)',
      symbol: 'φ',
      blurb: 'The ratio (1 + √5) / 2 found in art, nature and geometry.',
    },
  };

const ZERO = BigInt(0);
const ONE = BigInt(1);
const TWO = BigInt(2);
const TEN = BigInt(10);

/** Integer square root of a non-negative BigInt (Newton's method). */
export function bigIntSqrt(value: bigint): bigint {
  if (value < ZERO) {
    throw new Error('Cannot take the square root of a negative number.');
  }
  if (value < TWO) {
    return value;
  }
  let x = value;
  let y = (x + ONE) / TWO;
  while (y < x) {
    x = y;
    y = (x + value / x) / TWO;
  }
  return x;
}

/** 10 raised to an integer power as a BigInt. */
function pow10(exponent: number): bigint {
  let result = ONE;
  for (let i = 0; i < exponent; i += 1) {
    result *= TEN;
  }
  return result;
}

/**
 * Unbounded spigot algorithm for pi (Gibbons). Returns the requested number of
 * decimal digits, i.e. everything after the point, as a string.
 */
function piFractional(decimals: number): string {
  const total = decimals + 1; // first emitted digit is the integer part "3"
  const out: string[] = [];
  let q = ONE;
  let r = ZERO;
  let t = ONE;
  let k = ONE;
  let n = BigInt(3);
  let l = BigInt(3);

  while (out.length < total) {
    if (BigInt(4) * q + r - t < n * t) {
      out.push(n.toString());
      const nr = TEN * (r - n * t);
      n = (TEN * (BigInt(3) * q + r)) / t - TEN * n;
      q *= TEN;
      r = nr;
    } else {
      const nr = (TWO * q + r) * l;
      const nn = (q * (BigInt(7) * k + TWO) + r * l) / (t * l);
      q *= k;
      t *= l;
      l += TWO;
      k += ONE;
      n = nn;
      r = nr;
    }
  }

  // out[0] is "3" (integer part); drop it and keep the fractional digits.
  return out.slice(1).join('');
}

/**
 * Scaled integer value of e via the series sum 1/k!. Guard digits absorb the
 * truncation error from integer division so the returned fractional digits are
 * accurate.
 */
function eScaled(decimals: number): { integer: string; fractional: string } {
  const guard = 12;
  const scale = pow10(decimals + guard);
  let term = scale; // 1/0! scaled
  let sum = scale; // start the running total at 1
  let k = ONE;
  while (term > ZERO) {
    term = term / k;
    sum += term;
    k += ONE;
  }
  return splitScaled(sum, decimals, guard);
}

/** Scaled integer value of the golden ratio (1 + sqrt(5)) / 2. */
function phiScaled(decimals: number): { integer: string; fractional: string } {
  const guard = 12;
  const scale = pow10(decimals + guard);
  // sqrt(5) * scale = sqrt(5 * scale^2)
  const rootFive = bigIntSqrt(BigInt(5) * scale * scale);
  const value = (scale + rootFive) / TWO;
  return splitScaled(value, decimals, guard);
}

/**
 * Split a value scaled by 10^(decimals + guard) into an integer-part string and
 * `decimals` fractional digits, dropping the guard digits.
 */
function splitScaled(
  value: bigint,
  decimals: number,
  guard: number,
): { integer: string; fractional: string } {
  const digits = value.toString();
  const fractionalLength = decimals + guard;
  const padded = digits.padStart(fractionalLength + 1, '0');
  const integer = padded.slice(0, padded.length - fractionalLength);
  const fractionalFull = padded.slice(padded.length - fractionalLength);
  return { integer, fractional: fractionalFull.slice(0, decimals) };
}

export type ConstantDigitsResult =
  | { ok: true; integer: string; fractional: string; decimals: number }
  | { ok: false; message: string };

/**
 * Compute a math constant to `decimals` places after the decimal point.
 * `decimals` is clamped to [0, MAX_DIGITS].
 */
export function computeConstantDigits(
  constant: MathConstant,
  decimals: number,
): ConstantDigitsResult {
  if (!Number.isFinite(decimals)) {
    return { ok: false, message: 'Enter a valid number of digits.' };
  }
  const places = Math.max(0, Math.min(MAX_DIGITS, Math.floor(decimals)));

  try {
    if (constant === 'pi') {
      return { ok: true, integer: '3', fractional: piFractional(places), decimals: places };
    }
    if (constant === 'e') {
      const { integer, fractional } = eScaled(places);
      return { ok: true, integer, fractional, decimals: places };
    }
    const { integer, fractional } = phiScaled(places);
    return { ok: true, integer, fractional, decimals: places };
  } catch {
    return { ok: false, message: 'Could not compute that many digits. Try a smaller count.' };
  }
}

/** Format the result as a single string like "3.14159". */
export function formatConstant(result: { integer: string; fractional: string }): string {
  if (!result.fractional) {
    return result.integer;
  }
  return `${result.integer}.${result.fractional}`;
}
